import { createSlice } from '@reduxjs/toolkit'
import {
  BiddingStrategyTypes,
  ChangeAmountUnit,
  MaxBiddingAmountChangeTypes,
  Ranks,
} from 'features/Codes'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'

const initialState = {
  visible: false,
  isSubmitting: false,
  previewVisible: false,
  keywords: [], // {id, text, biddingStrategy, adGroupMaxBiddingAmount}
  form: {
    changeAmount: '',
    maxBiddingAmount: 70,
    maxBiddingAmountChangeType: MaxBiddingAmountChangeTypes.NO_CALCULATE,
    biddingStrategyType: BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING,
    changeAmountUnit: ChangeAmountUnit.PERCENTAGE,
    rank: Ranks.MO1,
    useRankAmount: false,
    rankAmount: '',
  },
  preview: {
    changeAmount: '',
    maxBiddingAmount: 70,
    maxBiddingAmountChangeType: MaxBiddingAmountChangeTypes.NO_CALCULATE,
    biddingStrategyType: BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING,
    changeAmountUnit: ChangeAmountUnit.PERCENTAGE,
    rank: Ranks.MO1,
    useRankAmount: false,
    rankAmount: '',
  },
  biddingAverages: [],
  biddingAverageLoading: false,
  biddingAverageLoaded: false,
}

export const fetchBiddingAverages = createAxiosThunk(
  'dashboard/keywordBiddingStrategyEditModal/fetchBiddingAverages',
  keywords => api.recommendKeyword.getBiddingAverages(keywords)
)

export const editKeywordBiddingStrategy = createAxiosThunk(
  'dashboard/keywordBiddingStrategyEditModal/keywordLandingUrlEditModal',
  commands => api.dashboard.patchKeywordsBiddingStrategy(commands)
)

export const keywordBiddingStrategyEditModalSlice = createSlice({
  name: 'dashboard/keywordBiddingStrategyEditModal',
  initialState,

  reducers: {
    hideKeywordBiddingStrategyEdit: () => initialState,
    /**
     * 키워드 일예산 일괄 변경 팝업 노출
     * @param {Object} state 생략
     * @param {Object[]} payload  [{id, text, biddingStrategy, adGroupMaxBiddingAmount}]
     */
    showKeywordBiddingStrategyEdit: (state, { payload: keywords }) => {
      state.keywords = keywords
      const hasSameValue =
        keywords.length > 1 &&
        keywords.every((value, index, array) => {
          const { biddingStrategy } = value
          const other = array[0].biddingStrategy
          return (
            biddingStrategy.maxBiddingAmount === other.maxBiddingAmount &&
            biddingStrategy.type === other.type
          )
        })
      if (keywords.length === 1 || hasSameValue) {
        const [first] = keywords
        const {
          biddingStrategy: { maxBiddingAmount, type },
        } = first
        state.form.biddingStrategyType = type
        if (type === BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING) {
          state.form.maxBiddingAmount = maxBiddingAmount
        }
      }
      state.visible = true
    },
    setMaxBiddingAmountChangeType: (state, { payload }) => {
      state.form.maxBiddingAmountChangeType = payload
      state.form.changeAmountUnit = ChangeAmountUnit.PERCENTAGE
      state.form.rank = Ranks.MO1
      state.form.useRankAmount = false
      state.previewVisible = false
    },
    setBiddingStrategyType: (state, { payload }) => {
      state.form.biddingStrategyType = payload
      state.form.maxBiddingAmountChangeType =
        MaxBiddingAmountChangeTypes.NO_CALCULATE
      state.form.changeAmountUnit = ChangeAmountUnit.PERCENTAGE
      state.form.rank = Ranks.MO1
      state.form.useRankAmount = false
      state.previewVisible = false
    },
    setChangeAmountUnit: (state, { payload }) => {
      state.form.changeAmountUnit = payload
    },
    setRank: (state, { payload }) => {
      state.form.rank = payload
    },
    setUseRankAmount: (state, { payload }) => {
      state.form.useRankAmount = payload
    },
    showPreview: (
      state,
      { payload: { maxBiddingAmount, changeAmount, rankAmount } }
    ) => {
      state.preview = {
        ...state.form,
        maxBiddingAmount,
        changeAmount,
        rankAmount,
      }
      state.previewVisible = true
    },
    hidePreview: state => {
      state.previewVisible = false
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchBiddingAverages.pending, state => {
      state.biddingAverageLoading = true
      state.biddingAverageLoaded = false
    })

    builder.addCase(
      fetchBiddingAverages.fulfilled,
      (state, { payload: { results } }) => {
        state.biddingAverages = results
        state.biddingAverageLoaded = true
        state.biddingAverageLoading = false
      }
    )

    builder.addCase(fetchBiddingAverages.rejected, state => {
      state.biddingAverageLoading = false
    })

    builder.addCase(editKeywordBiddingStrategy.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(editKeywordBiddingStrategy.fulfilled, state => {
      state.isSubmitting = false
    })

    builder.addCase(editKeywordBiddingStrategy.rejected, state => {
      state.isSubmitting = false
    })
  },
})

export default keywordBiddingStrategyEditModalSlice.reducer
export const {
  showKeywordBiddingStrategyEdit,
  hideKeywordBiddingStrategyEdit,
  setMaxBiddingAmountChangeType,
  setBiddingStrategyType,
  setChangeAmountUnit,
  setRank,
  setUseRankAmount,
  showPreview,
  hidePreview,
} = keywordBiddingStrategyEditModalSlice.actions
